import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../../App.css";
import { apiPostAuth, apiPostAuthSlow } from "../../utils/api";

export default function GooglePlayCheckoutPage() {
  const { product_id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);

  useEffect(() => {
    const fetchCheckoutData = async () => {
      try {
        const response = await apiPostAuth("google_apps/checkout_confirmation.php", {
          product_id: product_id,
        });
        if (response.result === 1) {
          setCheckoutData(response.data);
        } else {
          alert(response.message || "Failed to load checkout details.");
          navigate("/dashboard/billing-payment");
        }
      } catch (error) {
        alert("Unable to load checkout details. Please try again.");
        navigate("/dashboard/billing-payment");
      } finally {
        setLoading(false);
      }
    };

    fetchCheckoutData();
  }, [product_id, navigate]);

  const handleConfirmPurchase = async () => {
    setLoadingPayment(true)
    try {
        const response = await apiPostAuthSlow("google_apps/checkout_payment.php", {
        product_id: product_id,
        });

        if (response.result === 1 && response.payment_url) {
        window.location.href = response.payment_url; // Redirect to payment gateway
        } else {
        alert(response.message || "Failed to initiate payment. Please try again.");
        }
    } catch (error) {
        alert("Unable to start the payment process. Please try again.");
    }finally{
        setLoadingPayment(false);
    }
    };

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <div className="loading-spinner"></div>
        <p>Loading checkout details...</p>
      </div>
    );
  }

  if (!checkoutData) return null;

  return (
    <div style={{ padding: "20px" }}>
      <button
        onClick={() => navigate("/dashboard/billing-payment")}
        style={backButtonStyle}
      >
        ← Back to Billing & Payments
      </button>

      <h1 style={{ fontSize: "1.8rem", marginBottom: "20px", color: "#1f2937" }}>
        Checkout Confirmation
      </h1>

      {/* Invoice-style container */}
      <div style={invoiceContainer}>
        <h2 style={invoiceTitle}>Purchase Details</h2>

        <div style={invoiceRow}>
          <span>Product:</span>
          <span>{checkoutData.product_name}</span>
        </div>
        <div style={invoiceRow}>
          <span>Credits:</span>
          <span>{checkoutData.credit_amount} Credits</span>
        </div>

        {/* Price */}
        <div style={invoicePriceRow}>
          <span>Price:</span>
          <div style={{ textAlign: "right" }}>
            <span style={priceHighlight}>${checkoutData.price_usd}</span>
            <p style={priceSub}>≈ IDR {checkoutData.price_idr.toLocaleString()}</p>
          </div>
        </div>

        {/* Admin Fee */}
        <div style={invoiceRow}>
          <span>Admin Fee:</span>
          <div style={{ textAlign: "right" }}>
            <span style={priceHighlight}>${checkoutData.admin_fee_usd}</span>
            <p style={priceSub}>≈ IDR {checkoutData.admin_fee_idr.toLocaleString()}</p>
          </div>
        </div>
        <p style={noteText}>
          *Admin Fee is the payment gateway fee applied to your transaction.
        </p>

        {/* Total Amount */}
        <div style={invoiceTotalRow}>
          <span>Total Amount:</span>
          <div style={{ textAlign: "right" }}>
            <span style={priceHighlight}>${checkoutData.total_usd}</span>
            <p style={priceSub}>≈ IDR {checkoutData.total_idr.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <p style={usdNoteText}>
        *The USD amount is an estimate. We charge in IDR, so the USD equivalent
        may vary slightly depending on the exchange rate.
      </p>

      {/* Confirm Button */}
      <button
        className="primary-button"
        onClick={handleConfirmPurchase}
        style={{ marginTop: "20px", width: "100%" }}
      >
        {loadingPayment ? "Processing purchase..." : "Confirm & Purchase"}
      </button>
    </div>
  );
}

const invoiceContainer = {
  backgroundColor: "#fff",
  borderRadius: "8px",
  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  padding: "20px",
  marginBottom: "20px",
};

const invoiceTitle = {
  fontSize: "1.4rem",
  marginBottom: "15px",
  color: "#1f2937",
  borderBottom: "1px solid #e5e7eb",
  paddingBottom: "8px",
};

const invoiceRow = {
  display: "flex",
  justifyContent: "space-between",
  padding: "8px 0",
  fontSize: "1rem",
  color: "#374151",
};

const invoicePriceRow = {
  display: "flex",
  justifyContent: "space-between",
  padding: "12px 0",
  borderTop: "1px solid #e5e7eb",
  borderBottom: "1px solid #e5e7eb",
  fontSize: "1rem",
  color: "#374151",
};

const invoiceTotalRow = {
  display: "flex",
  justifyContent: "space-between",
  padding: "12px 0",
  fontWeight: "700",
  color: "#111827",
  fontSize: "1.1rem",
};

const priceHighlight = {
  fontSize: "1.4rem",
  fontWeight: "700",
  color: "#2563eb",
};

const priceSub = {
  fontSize: "0.9rem",
  color: "#6b7280",
  margin: 0,
};

const noteText = {
  fontSize: "0.85rem",
  color: "#6b7280",
  marginTop: "4px",
};

const usdNoteText = {
  fontSize: "0.85rem",
  color: "#6b7280",
  marginTop: "10px",
  textAlign: "center",
};

const backButtonStyle = {
  display: "inline-block",
  padding: "8px 16px",
  backgroundColor: "#f2f6fc",
  color: "#2563eb",
  fontWeight: "500",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  cursor: "pointer",
  marginBottom: "20px",
  transition: "all 0.2s ease",
};
